import React from "react";
import {
  IndicatorDefinition,
  IndicatorRegistry,
  BaseIndicatorConfig,
  BaseIndicatorProps,
} from "../interface/Indicator.interface";
import { debugWarn } from "../utils/debug";

/**
 * Singleton registry for managing chart indicators
 */
class IndicatorRegistryImpl implements IndicatorRegistry {
  private indicators = new Map<string, IndicatorDefinition>();

  register<T extends BaseIndicatorProps>(
    definition: IndicatorDefinition<T>
  ): void {
    if (this.indicators.has(definition.config.id)) {
      debugWarn(
        `Indicator with ID "${definition.config.id}" is already registered. Overwriting.`
      );
    }
    this.indicators.set(
      definition.config.id,
      definition as IndicatorDefinition
    );
  }

  get(id: string): IndicatorDefinition | undefined {
    return this.indicators.get(id);
  }

  getAll(): IndicatorDefinition[] {
    return Array.from(this.indicators.values());
  }

  getConfigs(): BaseIndicatorConfig[] {
    return this.getAll().map((def) => def.config);
  }

  has(id: string): boolean {
    return this.indicators.has(id);
  }

  /**
   * Unregister an indicator (useful for testing or hot reloading)
   */
  unregister(id: string): boolean {
    return this.indicators.delete(id);
  }

  /**
   * Clear all indicators (useful for testing)
   */
  clear(): void {
    this.indicators.clear();
  }
}

// Export singleton instance
export const indicatorRegistry = new IndicatorRegistryImpl();

/**
 * Helper function to create an indicator definition
 */
export function createIndicator<
  T extends BaseIndicatorProps = BaseIndicatorProps
>(
  config: BaseIndicatorConfig,
  component: (props: T) => React.ReactNode
): IndicatorDefinition<T> {
  return {
    config,
    component,
  };
}

/**
 * Hook to use the indicator registry
 */
export function useIndicatorRegistry() {
  return indicatorRegistry;
}
